/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/

import React, { useState, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Stack,
  Grid2,
  Container,
  Typography,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PopupDialog from "../PopupDialog";
import "./ApplicationDetailsIcon.css";
import { getConfig } from "../../config";

function ApplicationDetailsIcon({ callTheIconFileChange, submissionData }) {
  const [dimensionError, setDimensionError] = useState("");
  const [openErrorPopup, setOpenErrorPopup] = useState(false);
  const [componentName, setComponentName] = useState("");
  const [headerMessage, setHeaderMessage] = useState("");
  const thirdPartyApps = globalThis.location.href.includes("third-party-apps");
  const fileInputRef = useRef(null);
  const handleCloseDialog = () => {
    setOpenErrorPopup(false);
  };

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    validateAndSelectImage(file);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const file = event.dataTransfer.files[0];
    validateAndSelectImage(file);
  };

  const validateAndSelectImage = useCallback((file) => {
    if (!file) {
      return;
    }

    if (!file.type.startsWith("image/")) {
      setDimensionError("Please select an image file.");
      setHeaderMessage("Please select an image file.");
      setComponentName("appError");
      setOpenErrorPopup(true);
      return;
    }

    const img = new Image();
    img.onload = () => {
      if (img.width !== 512 || img.height !== 512) {
        setDimensionError("Image dimensions must be 512x512 pixels.");
        setHeaderMessage("Image dimensions must be 512x512 pixels.");
        setComponentName("appError");
        setOpenErrorPopup(true);
      } else {
        handleUploadImage(file);
      }
    };
    img.src = URL.createObjectURL(file);
  }, []);

  const handleUploadImage = (file) => {
    callTheIconFileChange(file, submissionData.id);
  };

  const handleClick = () => {
    fileInputRef.current.click(); // Trigger the file input click
  };

  return (
    <Container maxWidth="lg">
      <Grid2 container justifyContent="flex-start" alignItems="center">
        <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
          <div
            className={
              submissionData &&
              Object.keys(submissionData).length > 0 &&
              submissionData.status !== "DRAFT"
                ? "application-icon application-icon-border"
                : "application-icon"
            }
            style={{
              marginTop: "30px",
              borderBottom:
                submissionData &&
                Object.keys(submissionData).length > 0 &&
                submissionData.status !== "DRAFT"
                  ? "1px solid rgba(18, 20, 21, 0.2) !important"
                  : "0px !important",
              width:
                submissionData &&
                Object.keys(submissionData).length > 0 &&
                submissionData.status === "DRAFT"
                  ? "85%"
                  : "90%",
            }}
          >
            <Typography
              sx={{
                "font-weight": "bold !important",
                "font-size": "20px !important",
                // "letter-spacing": "0.4px !important",
                color: "rgba(18, 20, 21, 1) !important",
                marginLeft: "2px !important",
              }}
              className="uploadSignedApkText"
            >
              Application Icon
            </Typography>
            {submissionData &&
              Object.keys(submissionData).length > 0 &&
              submissionData.status === "DRAFT" && (
                <p className="application-iocn-text-guidelines">
                  Upload your App icon, please take a moment to review the
                  following{""}{" "}
                  <a
                    href="https://appdevelopers.harman.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="guideline-text"
                    style={{
                      color: "#0066cc",
                      textDecoration: "none",
                      cursor: "pointer",
                    }}
                  >
                    Guidelines
                    <OpenInNewIcon
                      sx={{ marginBottom: "-7px", fontSize: "22px" }}
                    />
                  </a>
                </p>
              )}
            {submissionData &&
              Object.keys(submissionData).length > 0 &&
              submissionData.status === "DRAFT" && (
                <>
                  <p className="application-iocn-text-guidelines next-guideline-text">
                    Following these guidelines will ensure your app icon looks
                    great and meets the required specifications.
                  </p>

                  <Typography
                    sx={{
                      fontSize: "12px",
                      color: "#5a5b5c",
                      marginTop: "-7px",
                      marginBottom: "15px",
                      display: "none",
                    }}
                  >
                    JPEG, JPE or PNG-24(without alpha channel). width 1024px .
                    height 500px.
                  </Typography>
                </>
              )}
            <Typography
              sx={{
                color: "#5a5b5c !important",
                fontSize: "14px",
                marginTop: submissionData.status === "DRAFT" ? "-10px" : "8px",
                marginBottom: "10px",
                marginLeft: "2px !important",
              }}
            >
              <b>Size:</b>{" "}
              <span style={{ fontSize: "12px" }}> 512 px X 512 px</span>
              <span style={{ marginRight: "15px" }}></span>
              <b>Format:</b>{" "}
              <span style={{ fontSize: "12px" }}> 32-bit PNG</span>
              <span style={{ marginRight: "15px" }}></span>
              <b>Max file size:</b>{" "}
              <span style={{ fontSize: "12px" }}>1024 KB</span>
              <span style={{ marginRight: "15px" }}></span>
              <b>Color space:</b> <span style={{ fontSize: "12px" }}>RGB</span>
              <span style={{ marginRight: "15px" }}></span>
              <b>Shape:</b>{" "}
              <span style={{ fontSize: "12px" }}>Full square</span>
            </Typography>

            <Typography
              sx={{
                fontSize: "12px",
                color: "#5a5b5c",
                marginTop: "-7px",
                marginBottom: "15px",
                display: "none",
              }}
            >
              JPEG, JPE or PNG-24(without alpha channel). width 1024px . height
              500px.
            </Typography>
            {submissionData &&
              Object.keys(submissionData).length > 0 &&
              submissionData.status === "DRAFT" && (
                <div className="upload-button-icon-wrap">
                  {
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={2}
                      sx={{
                        style: {
                          width: "100%",
                        },
                      }}
                    >
                      <button
                        className="upload-button-icon"
                        style={{
                          border: "2px dashed  rgba(18, 20, 21, 0.2)",
                          width: "100%",
                          textAlign: "center",
                          cursor: "pointer",
                          height: "60px !important",
                          background: "transparent",
                          padding: "0",
                        }}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        onClick={handleClick}
                      >
                        <input
                          type="file"
                          multiple
                          ref={fileInputRef}
                          onChange={handleImageChange}
                          style={{ display: "none" }}
                        />
                        <p className="application-iocn-button-text-guidelines">
                          Drop a PNG or JPG file here to upload or click to
                          browse
                        </p>
                      </button>
                    </Stack>
                  }

                  <div className="image-div">
                    <img
                      src={submissionData.icon.url}
                      width={110}
                      height={110}
                      alt={submissionData.icon.name}
                    />
                  </div>
                </div>
              )}
            {submissionData &&
              Object.keys(submissionData).length > 0 &&
              submissionData.status !== "DRAFT" && (
                <div className="image-div" style={{ marginTop: "15px" }}>
                  {(() => {
                    const portal = getConfig().REACT_APP_PORTAL;
                    const useDirectIcon =
                      portal === "oem-admin" ||
                      (portal === "domain-approver" && thirdPartyApps);
                    const iconSrc = useDirectIcon
                      ? submissionData.icon
                      : submissionData.icon.url;
                    return (
                      <img
                        src={iconSrc}
                        width={110}
                        height={110}
                        alt={submissionData?.icon?.name || "icon"}
                      />
                    );
                  })()}
                </div>
              )}
            {dimensionError && dimensionError !== "" && (
              <PopupDialog
                onClose={handleCloseDialog}
                openErrorPopup={openErrorPopup}
                headerMessage={headerMessage}
                componentName={componentName}
              />
            )}
          </div>
        </Grid2>
      </Grid2>
    </Container>
  );
}

ApplicationDetailsIcon.propTypes = {
  callTheIconFileChange: PropTypes.func.isRequired,
  submissionData: PropTypes.object.isRequired,
};

export default ApplicationDetailsIcon;
